// api/intakeApi.ts
import type { IntakeArtist } from "../types";

export class IntakeApiError extends Error {
  status: number; code: string; details?: Record<string, string[]>; retryAfter?: number;
  constructor(status: number, code: string, details?: Record<string, string[]>, retryAfter?: number) {
    super(code);
    this.status = status; this.code = code; this.details = details; this.retryAfter = retryAfter;
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new IntakeApiError(0, "network");
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new IntakeApiError(res.status, body.error ?? "error", body.details, body.retry_after ?? undefined);
  return body as T;
}

export async function fetchCommissionArtist(slug: string): Promise<IntakeArtist> {
  const { artist } = await request<{ artist: IntakeArtist }>(`/api/commission-artist/${encodeURIComponent(slug)}`);
  return artist;
}

export function submitIntake(payload: Record<string, unknown>) {
  return request<{ commission_id: string; tracking_url: string }>("/.netlify/functions/submit-commission", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
  });
}

export interface TrackingCommission {
  id: string; shortRef: string; status: string; createdAt: string;
  artist: { name: string; slug: string; profilePicture: string | null };
  commissionType: string | null; characterCount: number | null; backgroundType: string | null;
  usageType: string | null; contentRating: string | null; postingPermission: string | null;
  deadline: string | null; intake: unknown; estimatedTotal: number | null; estimateBreakdown: unknown;
  artistStatusAtSubmit: string | null;
}

export async function fetchTracking(token: string): Promise<TrackingCommission> {
  const { commission } = await request<{ commission: TrackingCommission }>(`/api/c/${encodeURIComponent(token)}`);
  return commission;
}

export async function resendLinks(email: string): Promise<void> {
  await request("/api/c/resend", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
}
